import React, { useState, useEffect } from 'react';
import { Container, Form, Button, Alert, Spinner, Row, Col, Card, Dropdown } from 'react-bootstrap';
import { useAuth } from '../context/AuthContext';

const API_BASE_URL = process.env.REACT_APP_BASE_API_URL;

const CsvUpload = () => {
    const { user, role } = useAuth(); 
    const isOperations = role === "operations"; 
    const [files, setFiles] = useState([]);
    const [status, setStatus] = useState('initial');
    const [message, setMessage] = useState('');
    const [uploading, setUploading] = useState(false);

    // Column Selection States
    const [availableColumns, setAvailableColumns] = useState([]);
    const [selectedColumns, setSelectedColumns] = useState([]);
    const [isLoadingColumns, setIsLoadingColumns] = useState(false);
    const [isGeneratingReport, setIsGeneratingReport] = useState(false);
    
    // Report States
    const [matchedTenders, setMatchedTenders] = useState(null);
    const [stats, setStats] = useState(null);
    const [showReportSection, setShowReportSection] = useState(false);
    const [reportStatus, setReportStatus] = useState('initial');
    const [reportMessage, setReportMessage] = useState('');
    
    // Customer Selection States (New)
    const [customers, setCustomers] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState(''); // Stores customer ID
    const [customerSearch, setCustomerSearch] = useState('');
    const [isLoadingCustomers, setIsLoadingCustomers] = useState(false);

    // Fetch available columns on component mount
    useEffect(() => {
        fetchAvailableColumns();
        if (isOperations) {
            fetchCustomers();
        }
    }, []);

    // Fetch available columns from backend
    const fetchAvailableColumns = async () => {
        setIsLoadingColumns(true);
        try {
            const response = await fetch(`${API_BASE_URL}/api/excel/columns`);
            if (response.ok) {
                const data = await response.json();
                if (data.success) {
                    setAvailableColumns(data.columns);
                    // By default, select ALL columns
                    const allSelected = data.columns.map(col => col.id);
                    setSelectedColumns(allSelected);
                }
            }
        } catch (error) {
            console.error('Error fetching columns:', error);
        } finally {
            setIsLoadingColumns(false);
        }
    };

    // Fetch customers for operations team (New)
    const fetchCustomers = async () => {
        setIsLoadingCustomers(true);
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/api/customers`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            if (response.ok) {
                const data = await response.json();
                if (data.success && Array.isArray(data.customers)) {
                    setCustomers(data.customers);
                } else if (Array.isArray(data)) { // Fallback for direct array response
                    setCustomers(data);
                }
            } else {
                console.error('Failed to fetch customers:', response.statusText);
            }
        } catch (error) {
            console.error('Error fetching customers:', error);
        } finally {
            setIsLoadingCustomers(false);
        }
    };

    // Handle file selection
    const handleFileChange = (e) => {
        if (e.target.files && e.target.files.length > 0) {
            setFiles(Array.from(e.target.files));
            setStatus('initial');
            setMessage('');
            setShowReportSection(false);
            setMatchedTenders(null);
            setStats(null);
            setReportStatus('initial');
            setReportMessage('');
        } else {
            setFiles([]);
        }
    };

    // Handle checkbox change for column selection
    const handleColumnToggle = (columnId) => {
        setSelectedColumns(prev => {
            if (prev.includes(columnId)) {
                return prev.filter(id => id !== columnId);
            } else {
                return [...prev, columnId];
            }
        });
    };

    // Handle select all / deselect all
    const handleSelectAll = () => {
        if (selectedColumns.length === availableColumns.length) {
            setSelectedColumns([]);
        } else {
            setSelectedColumns(availableColumns.map(col => col.id));
        }
    };

    // ✅ Upload files with selected columns
    const handleUpload = async () => {
        if (files.length === 0) {
            setMessage('Please select at least one file.');
            setStatus('error');
            return;
        }

        if (selectedColumns.length === 0) {
            setMessage('Please select at least one column for the Excel report.');
            setStatus('error');
            return;
        }

        // Operations team must select a customer
        if (isOperations && !selectedCustomer) {
            setMessage('Please select a customer before uploading.');
            setStatus('error');
            return;
        }

        setStatus('uploading');
        setUploading(true);
        setMessage('');

        const formData = new FormData();
        files.forEach(file => {
            formData.append('files', file);
        });

        // Get current user info from localStorage
        const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
        if (currentUser.id) {
            formData.append('userId', currentUser.id);
            formData.append('userEmail', currentUser.email || '');
            formData.append('userName', currentUser.full_name || '');
        } else {
            setStatus('error');
            setMessage('Please login first');
            setUploading(false);
            return;
        }
        
        // ✅ Append selected customer ID if operations team is uploading
        if (isOperations && selectedCustomer) {
            formData.append('customerId', selectedCustomer);
        }

        // ✅ Append selected columns to form data
        formData.append('selectedColumns', JSON.stringify(selectedColumns));

        try {
            const response = await fetch(`${API_BASE_URL}/api/watchlist/upload`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}` // Add Authorization header
                },
                body: formData,
            });

            if (!response.ok) {
                let errorMessage = `Upload failed with status: ${response.status}`;
                try {
                    const errorData = await response.json();
                    errorMessage = errorData.message || errorData.error || errorMessage;
                } catch (jsonError) {
                    console.error("The server response was not valid JSON.");
                }
                throw new Error(errorMessage);
            }

            const responseData = await response.json();
            console.log('📦 Upload Response:', responseData);
            
            setStatus('success');
            setMessage(responseData.message || `${files.length} file(s) uploaded successfully!`);
            
            // Clear file input
            setFiles([]);
            const fileInput = document.getElementById('formFile');
            if (fileInput) fileInput.value = '';

        } catch (err) {
            setStatus('error');
            setMessage(err.message || 'An unknown error occurred.');
        } finally {
            setUploading(false);
        }
    };

    // Fetch matched tenders from backend
    const fetchMatchedTenders = async () => {
        try {
            const user = JSON.parse(localStorage.getItem('user') || '{}');
            const url = `${API_BASE_URL}/api/watchlist/matched${user.id ? `?userId=${user.id}` : ''}`;
            
            const response = await fetch(url);
            if (response.ok) {
                const data = await response.json();
                if (data.success) {
                    setMatchedTenders(data.data);
                    setStats(data.stats);
                    setShowReportSection(true);
                    setReportStatus('initial');
                    setReportMessage('');
                }
            }
        } catch (error) {
            console.error('Error fetching matched tenders:', error);
        }
    };

    // ✅ Generate Excel with selected columns (if not auto-generated)
    const generateExcelReport = async () => {
        const columnsToUse = selectedColumns.length > 0 ? selectedColumns : availableColumns.map(col => col.id);

        if (columnsToUse.length === 0) {
            setReportStatus('error');
            setReportMessage('Please select at least one column');
            return;
        }

        if (!matchedTenders || Object.keys(matchedTenders).length === 0) {
            setReportStatus('error');
            setReportMessage('No matched tenders data available');
            return;
        }

        setIsGeneratingReport(true);
        setReportStatus('loading');
        setReportMessage('Generating Excel report...');

        try {
            const response = await fetch(`${API_BASE_URL}/api/excel/generate`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    selectedColumns: columnsToUse,
                    matchedTenders: matchedTenders,
                    stats: stats || { totalFiles: 0, totalKeywords: 0, uploadedFiles: [] }
                })
            });

            if (!response.ok) {
                throw new Error(`Failed to generate report: ${response.status}`);
            }

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            const timestamp = new Date().toISOString().replace(/[:.]/g, '-').split('T')[0];
            link.setAttribute('download', `tender_report_${timestamp}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            setReportStatus('success');
            setReportMessage(`✅ Excel report generated with ${columnsToUse.length} columns!`);

        } catch (error) {
            console.error('Error generating report:', error);
            setReportStatus('error');
            setReportMessage(error.message || 'Failed to generate report. Please try again.');
        } finally {
            setIsGeneratingReport(false);
        }
    };

    const totalTenders = matchedTenders ? 
        Object.values(matchedTenders).reduce((sum, arr) => sum + arr.length, 0) : 0;

    return (
        <Container className="py-4">
            <h2>Upload Watchlist Files</h2>
            <p>Select your files, choose the columns you want in the Excel report, then upload.</p>

            {/* Status Messages */}
            {status === 'success' && <Alert variant="success" onClose={() => setStatus('initial')} dismissible>{message}</Alert>}
            {status === 'error' && <Alert variant="danger" onClose={() => setStatus('initial')} dismissible>{message}</Alert>}

            {/* File Upload */}
            <Form.Group controlId="formFile" className="mb-3">
                <Form.Label>Choose Files</Form.Label>
                <Form.Control 
                    type="file" 
                    accept=".csv,.xls,.xlsx"
                    multiple
                    onChange={handleFileChange} 
                    disabled={status === 'uploading' || uploading} 
                />
                <Form.Text className="text-muted">
                    Supported formats: CSV, XLS, XLSX (Max 50MB per file)
                </Form.Text>
            </Form.Group>

            {/* Selected Files List */}
            {files.length > 0 && (
                <div className="mt-3 mb-3 selected-files-list">
                    <strong>Selected Files:</strong>
                    <ul>
                        {files.map((file, index) => (
                            <li key={index}>{file.name} ({(file.size / 1024).toFixed(2)} KB)</li>
                        ))}
                    </ul>
                </div>
            )}

            {/* Customer Selection (Operations Team Only) */}
            {isOperations && (
                <Form.Group controlId="formCustomer" className="mb-3">
                    <Form.Label>Select Customer</Form.Label>
                    <Dropdown drop="down" className="w-100">
                        <Dropdown.Toggle 
                            variant="outline-secondary" 
                            className="w-100 d-flex justify-content-between align-items-center bg-white text-dark text-start border" 
                            disabled={uploading || isLoadingCustomers}
                        >
                            <div className="d-flex align-items-center flex-grow-1 text-truncate">
                                <span className="text-truncate">
                                    {selectedCustomer ? customers.find(c => c.id === selectedCustomer)?.full_name || "Select a customer" : "Select a customer"}
                                </span>
                                {selectedCustomer && (
                                    <i 
                                        className="bi bi-x fs-5 text-muted ms-1" 
                                        style={{ cursor: "pointer", zIndex: 10, lineHeight: 1 }}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            setSelectedCustomer("");
                                        }}
                                        title="Clear selection"
                                    ></i>
                                )}
                            </div>
                            <i className="bi bi-chevron-down text-muted ms-2" style={{ fontSize: "14px" }}></i>
                        </Dropdown.Toggle>
                        <Dropdown.Menu className="w-100 pt-0 pb-2" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                            <div className="px-3 pb-2 pt-3 position-sticky top-0 bg-white" style={{ zIndex: 10, borderBottom: '1px solid #eaedf1', marginBottom: '0.5rem' }} onClick={(e) => e.stopPropagation()}>
                                <Form.Control
                                    type="text"
                                    placeholder="Search customer..."
                                    value={customerSearch}
                                    onChange={(e) => setCustomerSearch(e.target.value)}
                                    autoFocus
                                />
                            </div>
                            {isLoadingCustomers ? (
                                <Dropdown.Item disabled>Loading customers...</Dropdown.Item>
                            ) : (
                                customers.filter(customer =>
                                    customer.full_name.toLowerCase().includes(customerSearch.toLowerCase())
                                ).map(customer => (
                                    <Dropdown.Item key={customer.id} onClick={() => setSelectedCustomer(customer.id)}>
                                        {customer.full_name} ({customer.email})
                                    </Dropdown.Item>
                                ))
                            )}
                        </Dropdown.Menu>
                    </Dropdown>
                </Form.Group>
            )}

            {/* ✅ COLUMN SELECTION - SHOW BEFORE UPLOAD */}
            {files.length > 0 && (
                <Card className="mb-3">
                    <Card.Header style={{ background: 'linear-gradient(135deg, #1a5276 0%, #2e86c1 100%)', color: 'white' }}>
                        <h5 className="mb-0">📋 Select Columns for Excel Report</h5>
                    </Card.Header>
                    <Card.Body>
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <span className="text-muted">
                                ({selectedColumns.length} of {availableColumns.length} selected)
                            </span>
                            <Button 
                                variant="outline-primary" 
                                size="sm"
                                onClick={handleSelectAll}
                                disabled={isLoadingColumns}
                            >
                                {selectedColumns.length === availableColumns.length ? 'Deselect All' : 'Select All'}
                            </Button>
                        </div>

                        {isLoadingColumns ? (
                            <div className="text-center py-3">
                                <Spinner animation="border" size="sm" />
                                <span className="ms-2">Loading columns...</span>
                            </div>
                        ) : (
                            <>
                                <div className="columns-grid" style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                                    gap: '8px',
                                    maxHeight: '250px',
                                    overflowY: 'auto',
                                    padding: '10px',
                                    background: '#f8f9fa',
                                    borderRadius: '4px',
                                    border: '1px solid #e0e0e0'
                                }}>
                                    {availableColumns.map((column) => (
                                        <div key={column.id} className="column-checkbox" style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            padding: '6px 10px',
                                            borderRadius: '4px',
                                            transition: 'background 0.2s',
                                            cursor: 'pointer'
                                        }}>
                                            <input
                                                type="checkbox"
                                                id={`col-${column.id}`}
                                                checked={selectedColumns.includes(column.id)}
                                                onChange={() => handleColumnToggle(column.id)}
                                                style={{ marginRight: '10px' }}
                                            />
                                            <label 
                                                htmlFor={`col-${column.id}`}
                                                style={{ 
                                                    cursor: 'pointer', 
                                                    fontSize: '13px',
                                                    margin: 0,
                                                    flex: 1
                                                }}
                                            >
                                                {column.label}
                                            </label>
                                        </div>
                                    ))}
                                </div>
                                <div className="mt-2">
                                    <small className="text-muted">
                                        {selectedColumns.length === 0 ? 
                                            '⚠️ Please select at least one column.' : 
                                            `✅ ${selectedColumns.length} columns selected for export`
                                        }
                                    </small>
                                </div>
                            </>
                        )}
                    </Card.Body>
                </Card>
            )}

            {/* Upload Button - Disabled if no files or no columns selected */}
            <Button 
                variant="primary" 
                onClick={handleUpload} 
                disabled={
                    files.length === 0 ||
                    selectedColumns.length === 0 ||
                    status === 'uploading' ||
                    uploading ||
                    (isOperations && !selectedCustomer) // Disable if operations and no customer selected
                }
                style={{ width: '100%', backgroundColor: '#2F7A87', borderColor: '#2F7A87' }}
            >
                {status === 'uploading' || uploading ? (
                    <>
                        <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                        {' Uploading...'}
                    </>
                ) : (
                    `Upload ${files.length} File(s) with ${selectedColumns.length} Columns`
                )}
            </Button>

            {files.length > 0 && selectedColumns.length === 0 && (
                <Alert variant="warning" className="mt-2">
                    ⚠️ Please select at least one column before uploading.
                </Alert>
            )}

            {/* ===== REPORT SECTION (After Upload) ===== */}
            {showReportSection && matchedTenders && Object.keys(matchedTenders).length > 0 && (
                <Card className="mt-4">
                    <Card.Header style={{ background: 'linear-gradient(135deg, #1a5276 0%, #2e86c1 100%)', color: 'white' }}>
                        <h4 className="mb-0">📊 Upload Complete! Generate Excel Report</h4>
                    </Card.Header>
                    <Card.Body>
                        {/* Summary Stats */}
                        <Row className="mb-3">
                            <Col md={4}>
                                <div className="p-3 bg-light rounded text-center">
                                    <h5>Categories</h5>
                                    <h3 className="text-primary">{Object.keys(matchedTenders).length}</h3>
                                </div>
                            </Col>
                            <Col md={4}>
                                <div className="p-3 bg-light rounded text-center">
                                    <h5>Total Tenders</h5>
                                    <h3 className="text-success">{totalTenders}</h3>
                                </div>
                            </Col>
                            <Col md={4}>
                                <div className="p-3 bg-light rounded text-center">
                                    <h5>Keywords</h5>
                                    <h3 className="text-info">{stats?.totalKeywords || 0}</h3>
                                </div>
                            </Col>
                        </Row>

                        {/* Generate Button */}
                        <Button 
                            variant="success" 
                            onClick={generateExcelReport}
                            disabled={isGeneratingReport}
                            style={{ width: '100%' }}
                        >
                            {isGeneratingReport ? (
                                <>
                                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                                    {' Generating Excel...'}
                                </>
                            ) : (
                                `📥 Generate Excel (${selectedColumns.length} Columns)`
                            )}
                        </Button>

                        {/* Report Status Messages */}
                        {reportStatus === 'loading' && (
                            <Alert variant="info" className="mt-3">
                                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                                {' ' + reportMessage}
                            </Alert>
                        )}
                        {reportStatus === 'success' && (
                            <Alert variant="success" className="mt-3" onClose={() => setReportStatus('initial')} dismissible>
                                ✅ {reportMessage}
                            </Alert>
                        )}
                        {reportStatus === 'error' && (
                            <Alert variant="danger" className="mt-3" onClose={() => setReportStatus('initial')} dismissible>
                                ❌ {reportMessage}
                            </Alert>
                        )}

                        {/* File Info */}
                        {stats?.uploadedFiles && stats.uploadedFiles.length > 0 && (
                            <div className="mt-3">
                                <small className="text-muted">
                                    <strong>Watchlist Files:</strong>{' '}
                                    {stats.uploadedFiles.map(f => f.name).join(', ')}
                                </small>
                            </div>
                        )}
                    </Card.Body>
                </Card>
            )}

            <hr className="mt-4" />
            <footer className="text-center text-muted">
                <small>
                    Daily email alerts automatically sent at 10:30 AM
                </small>
            </footer>

            {/* Custom CSS */}
            <style>{`
                .column-checkbox:hover {
                    background: #e9ecef;
                }
                .columns-grid::-webkit-scrollbar {
                    width: 6px;
                }
                .columns-grid::-webkit-scrollbar-track {
                    background: #f1f1f1;
                    border-radius: 3px;
                }
                .columns-grid::-webkit-scrollbar-thumb {
                    background: #c1c1c1;
                    border-radius: 3px;
                }
                .columns-grid::-webkit-scrollbar-thumb:hover {
                    background: #a8a8a8;
                }
            `}</style>
        </Container>
    );
};

export default CsvUpload;